// Vue Pipeline — port de rec2-pipeline.jsx (kanban drag-to-advance),
// rec2-views.jsx (Liste triable + « Balle dans le camp ») et du
// sélecteur de demandes de rec12-shell. Phase 3 ajoute le comparatif
// finalistes (moyennes du jury côte à côte). Déviations: les colonnes
// Offre/Recruté existent mais refusent le drop (constructeur d'offre en
// phase 4), « À traiter » remplace le « À vous » persona.
import { useMemo, useState } from 'react';
import { ENTITIES, REC_DIMS, REC_STAGES } from '@pointage/shared';
import type { RecApplicationDTO, RecRequisitionDTO, RecRoleDTO, RecStageId } from '../../api/hooks.js';
import {
  RecAv, RecDaysPill, RecEnt, RecEstPill, recDays, recNextAction, recStageOf,
} from './common.js';
import { BarPill, Radar, fmt1 } from './V2Ui.js';
import { useRecSt } from './V2Sections.js';

const ACTIVE = new Set(['NOUVELLE', 'PRESEL', 'APPEL', 'TEST', 'ENTRETIEN', 'FINALISTE', 'OFFRE', 'RECRUTE']);
const MOVABLE = new Set(['NOUVELLE', 'PRESEL', 'APPEL', 'TEST', 'ENTRETIEN', 'FINALISTE']);

// v2 — pied de carte : cas pratique (compte à rebours / à noter / note)
// ou mini-jauges du jury par dimension + verdict de barre.
function CardFoot({ app }: { app: RecApplicationDTO }) {
  const st = useRecSt();
  const t = app.test;
  if (app.stage === 'TEST' && t) {
    if (t.status === 'RENDU') return <div className="rv2-cardbar"><span className="rv2-tchip hot">🧪 rendu — à noter</span></div>;
    if (t.status === 'ENVOYE' && t.dueAt) {
      const h = Math.max(0, Math.round((new Date(t.dueAt).getTime() - Date.now()) / 36e5));
      return <div className="rv2-cardbar"><span className="rv2-tchip info">⏳ {h} h restantes</span></div>;
    }
    if (t.status === 'NOTE') return <div className="rv2-cardbar"><span className={`rv2-tchip ${app.gates.test.pass ? 'ok' : 'hot'}`}>🧪 {fmt1(t.score)}/5</span></div>;
    return <div className="rv2-cardbar"><span className="rv2-tchip info">brief à envoyer</span></div>;
  }
  if (app.stage === 'APPEL' && app.call?.reportedAt && !app.call.completedAt) {
    return <div className="rv2-cardbar"><span className="rv2-tchip ok" data-testid="rec-card-call-reported">✓ Appel fait le {recDoneDay(app.call.reportedAt)} — à vérifier</span><span className="rv2-cardby">{app.call.reportedBy}</span></div>;
  }
  const g = app.gates;
  if (!g.jury || g.masked || g.jury.overall == null) return null;
  return (
    <div className="rv2-cardbar">
      <span className="rv2-mini4">
        {REC_DIMS.map((d) => {
          const v = g.jury!.dims[d.id];
          const thr = d.id === 'PERFORMANCE' ? st.barPerf : st.barMin;
          return <i key={d.id} className={v != null && v < thr ? 'ko' : ''} style={{ '--dc': d.c, '--w': `${((v ?? 0) / 5) * 100}%` } as React.CSSProperties} title={`${d.lbl} ${fmt1(v)}`}></i>;
        })}
      </span>
      <span className="s">{fmt1(g.jury.overall)}</span>
      <BarPill bar={g.bar} compact overridden={g.overridden} st={st} />
    </div>
  );
}

export function recActiveApps(apps: RecApplicationDTO[], reqId: string) {
  return apps.filter((a) => a.requisitionId === reqId && ACTIVE.has(a.stage));
}

// Étape faite côté manager, en attente de vérification (« Appel fait —
// à vérifier ») : la carte passe sous celles qui restent à traiter.
export const recStepDone = (a: RecApplicationDTO) =>
  a.stage === 'APPEL' && !!a.call?.reportedAt && !a.call.completedAt;
// Ordre dans une étape : à traiter d'abord, la plus ancienne dans
// l'étape en tête ; les faites ensuite, la plus récemment faite en tête.
export function recWorkOrder(x: RecApplicationDTO, y: RecApplicationDTO) {
  const dx = recStepDone(x), dy = recStepDone(y);
  if (dx !== dy) return dx ? 1 : -1;
  return dx ? y.call!.reportedAt!.localeCompare(x.call!.reportedAt!) : x.stageSince.localeCompare(y.stageSince);
}
// « 22/10 » — date locale du signalement « appel fait ».
export const recDoneDay = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

// ── sélecteur de demandes ouvertes ──────────────────────────
export function RecReqSelector({ reqs, apps, sel, onSel }: {
  reqs: RecRequisitionDTO[];
  apps: RecApplicationDTO[];
  sel: string | null;
  onSel: (id: string) => void;
}) {
  const open = reqs.filter((r) => ['ouverte', 'validee'].includes(r.status));
  const pending = reqs.filter((r) => r.status === 'en_attente');
  return (
    <div className="rec-reqrow">
      {open.map((r) => {
        const ent = ENTITIES.find((e) => e.id === r.entityId);
        const n = recActiveApps(apps, r.id).length;
        return (
          <button key={r.id} className={`rec-req${sel === r.id ? ' on' : ''}`} onClick={() => onSel(r.id)}>
            <div>
              <div className="ttl">{r.roleLabel}</div>
              <div className="mt">
                {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
                {r.contractType === 'CDI' ? 'CDI' : `CDD ${r.cddMonths} mois`} · {r.seniority}
                {r.status === 'validee' && <span className="rec-stpill info">annonce à publier</span>}
              </div>
            </div>
            <span className="cnt">{n}<i>candidats</i></span>
          </button>
        );
      })}
      {pending.map((r) => {
        const ent = ENTITIES.find((e) => e.id === r.entityId);
        return (
          <div key={r.id} className="rec-req ghosted">
            <div>
              <div className="ttl">{r.roleLabel}</div>
              <div className="mt">
                {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
                <span className="rec-stpill warn">en attente de validation</span>
              </div>
            </div>
          </div>
        );
      })}
      {!open.length && !pending.length && (
        <div className="rec-emptybox" style={{ flex: 1 }}>📭 Aucun poste ouvert dans ce périmètre.</div>
      )}
    </div>
  );
}

// ── carte candidat (kanban) ─────────────────────────────────
function RecCard({ app, color, canEdit, onOpen, onDrag, onDragEnd, dragging }: {
  app: RecApplicationDTO;
  color: string;
  canEdit: boolean;
  onOpen: () => void;
  onDrag: () => void;
  onDragEnd: () => void;
  dragging: boolean;
}) {
  const c = app.candidate;
  return (
    <div
      className={`rec-card${dragging ? ' drag' : ''}`}
      style={{ '--sc': color } as React.CSSProperties}
      draggable={canEdit && MOVABLE.has(app.stage)}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDrag(); }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
    >
      <div className="top">
        <RecAv app={app} sm />
        <div style={{ minWidth: 0 }}>
          <div className="nm">{c.firstName} {c.lastName}</div>
          <div className="rl">{c.expSummary ?? c.city ?? ''}</div>
        </div>
      </div>
      {c.tags.length > 0 && (
        <div className="tags">{c.tags.slice(0, 3).map((t) => <span key={t} className="rec-tag">{t}</span>)}</div>
      )}
      <div className="bot">
        <RecDaysPill since={app.stageSince} />
        <span className="rec-src">{app.source}{app.referredBy ? ` · ${app.referredBy}` : ''}</span>
        {app.waitOn && canEdit && <span className="rec-wait"><i></i>À traiter</span>}
      </div>
      <CardFoot app={app} />
    </div>
  );
}

// ── kanban ──────────────────────────────────────────────────
export function RecBoard({ apps, reqId, canEdit, onOpen, onMove }: {
  apps: RecApplicationDTO[];
  reqId: string;
  canEdit: boolean;
  onOpen: (id: string) => void;
  onMove: (id: string, stage: RecStageId) => void;
}) {
  const [over, setOver] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const list = recActiveApps(apps, reqId);
  return (
    <div className="rec-board">
      {REC_STAGES.map((st) => {
        const col = list.filter((a) => a.stage === st.id).sort(recWorkOrder);
        return (
          <div
            key={st.id}
            className={`rec-col${over === st.id ? ' over' : ''}`}
            style={{ '--sc': st.c } as React.CSSProperties}
            onDragOver={(e) => { e.preventDefault(); setOver(st.id); }}
            onDragLeave={() => setOver((o) => (o === st.id ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              if (dragId) onMove(dragId, st.id as RecStageId);
              setDragId(null);
            }}
          >
            <div className="rec-col-h"><span className="d"></span>{st.lbl}<span className="n">{col.length}</span></div>
            <div className="rec-col-b">
              {col.map((a) => (
                <RecCard
                  key={a.id} app={a} color={st.c} canEdit={canEdit}
                  onOpen={() => onOpen(a.id)}
                  onDrag={() => setDragId(a.id)} onDragEnd={() => setDragId(null)}
                  dragging={dragId === a.id}
                />
              ))}
              {!col.length && <div className="rec-empty-col">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── liste triable ───────────────────────────────────────────
export function RecList({ apps, reqId, canEdit, onOpen }: {
  apps: RecApplicationDTO[];
  reqId: string;
  canEdit: boolean;
  onOpen: (id: string) => void;
}) {
  const [sort, setSort] = useState<[string, 1 | -1]>(['stageIdx', 1]);
  const [k, dir] = sort;
  const list = useMemo(() => recActiveApps(apps, reqId)
    .map((a) => ({
      a,
      nom: `${a.candidate.lastName} ${a.candidate.firstName}`,
      stageIdx: REC_STAGES.findIndex((s) => s.id === a.stage),
      days: recDays(a.stageSince),
      src: a.source,
    }))
    .sort((x, y) => {
      const xv = x[k as 'nom' | 'stageIdx' | 'days' | 'src'];
      const yv = y[k as 'nom' | 'stageIdx' | 'days' | 'src'];
      return (xv > yv ? 1 : xv < yv ? -1 : 0) * dir || (k === 'stageIdx' ? recWorkOrder(x.a, y.a) : 0);
    }), [apps, reqId, k, dir]);
  const th = (key: string, lbl: string, num?: boolean) => (
    <th className={num ? 'num' : ''} onClick={() => setSort([key, k === key ? (-dir as 1 | -1) : 1])}>
      {lbl}<span className={`caret${k === key ? ' on' : ''}`}>{dir === 1 ? '▲' : '▼'}</span>
    </th>
  );
  return (
    <div className="rec-tblwrap">
      <div className="rec-tblscroll">
        <table className="rec-tbl">
          <thead>
            <tr>{th('nom', 'Candidat')}{th('stageIdx', 'Étape')}{th('days', 'Jours', true)}{th('src', 'Source')}<th style={{ cursor: 'default' }}>Prochaine action</th><th className="num" style={{ cursor: 'default' }}></th></tr>
          </thead>
          <tbody>
            {list.map(({ a, days }, i) => (
              <tr key={a.id} style={{ animationDelay: `${i * 35}ms` }} onClick={() => onOpen(a.id)}>
                <td>
                  <div className="rec-emp">
                    <RecAv app={a} sm />
                    <div className="meta">
                      <span className="name">{a.candidate.firstName} {a.candidate.lastName}</span>
                      <span className="sub">{a.candidate.expSummary ?? a.candidate.city ?? ''}</span>
                    </div>
                  </div>
                </td>
                <td><RecEstPill stage={a.stage} /></td>
                <td className="num"><span className={`rec-days${days > 9 ? ' bad' : days > 4 ? ' warn' : ''}`}>{days} j</span></td>
                <td style={{ textTransform: 'capitalize', fontSize: 12 }}>{a.source}</td>
                <td style={{ fontSize: 12 }}>
                  {recNextAction(a)}
                  {a.waitOn && canEdit && <span className="rec-wait" style={{ marginLeft: 8 }}><i></i>À traiter</span>}
                </td>
                <td className="num" style={{ color: 'var(--shf)', fontWeight: 800 }}>→</td>
              </tr>
            ))}
            {!list.length && (
              <tr><td colSpan={6}><div className="rec-emptybox" style={{ border: 0 }}>Aucune candidature active sur ce poste.</div></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <span className="rec-foot-lbl" style={{ padding: '0 14px 10px', display: 'block' }}>
        {list.length} candidature{list.length > 1 ? 's' : ''} active{list.length > 1 ? 's' : ''} · cliquez un en-tête pour trier, une ligne pour ouvrir la fiche
      </span>
    </div>
  );
}

// ── comparatif face à la barre (v2) ─────────────────────────
// Chaque candidat comparé à la barre, jamais aux autres : radar des 4
// dimensions (moyenne du jury) sur le polygone de la barre, cas
// pratique, vérification, vote du manager recruteur. Pas de « meilleure
// carte ». Un juré qui n'a pas soumis voit les moyennes masquées.
export function RecCompare({ apps, reqId, onOpen }: {
  apps: RecApplicationDTO[];
  reqId: string;
  role?: RecRoleDTO | undefined;
  onOpen: (id: string) => void;
}) {
  const st = useRecSt();
  const rows = useMemo(
    () => apps.filter((a) => a.requisitionId === reqId && ['ENTRETIEN', 'FINALISTE', 'OFFRE'].includes(a.stage)),
    [apps, reqId],
  );
  if (!rows.length) {
    return <div className="rec-emptybox big">🏁 Personne à comparer — le comparatif s’ouvre quand des candidats atteignent l’étape entretien.</div>;
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="rv2-legend"><span><i></i>la barre (Perf. {fmt1(st.barPerf)}, autres {fmt1(st.barMin)}, global {fmt1(st.barOverall)})</span><span><i className="c"></i>moyenne du jury</span><span>● rouge = dimension sous son seuil</span></div>
      <div className="rv2-cmp">
        {rows.map((a, i) => {
          const g = a.gates;
          const stg = recStageOf(a.stage);
          const subs = a.interviews.flatMap((x) => x.scorecards ?? (x.myScorecard ? [x.myScorecard] : []));
          const state = g.masked ? 'pend' : g.bar?.state ?? 'pend';
          return (
            <div key={a.id} className={`rv2-cmpc ${state}`} style={{ animationDelay: `${i * 80}ms` }} onClick={() => onOpen(a.id)}>
              <div className="hd">
                <RecAv app={a} />
                <div style={{ minWidth: 0 }}><b>{a.candidate.firstName} {a.candidate.lastName}</b><span>{stg.lbl} · {subs.length} scorecard{subs.length > 1 ? 's' : ''}</span></div>
                <span className="sc" style={{ color: state === 'ok' ? 'var(--greenc)' : state === 'ko' ? 'var(--badc)' : 'var(--ink3)' }}>{g.masked ? '🙈' : fmt1(g.jury?.overall)}<i>/5</i></span>
              </div>
              {g.jury && !g.masked
                ? <div style={{ margin: '10px 0 2px' }}><Radar s={g.jury} st={st} color={a.candidate.avColor} size={210} /></div>
                : <div className="rec-emptybox" style={{ margin: '12px 0' }}>{g.masked ? '🙈 Masqué tant que votre scorecard n’est pas soumise' : 'Scorecards en attente'}</div>}
              <div style={{ display: 'flex', justifyContent: 'center' }}><BarPill bar={g.masked ? null : g.bar} overridden={g.overridden} st={st} /></div>
              {!g.masked && g.bar?.state === 'ko' && <div className="rec-hint" style={{ textAlign: 'center', color: 'var(--badc)', marginTop: 6 }}>{g.bar.fails.join(' · ')}</div>}
              <div className="facts">
                <div><b style={{ color: g.test.pass ? 'var(--greenc)' : 'var(--badc)' }}>{fmt1(g.test.score)}</b><span>cas pratique</span></div>
                <div><b style={{ color: g.verif.ok ? 'var(--greenc)' : 'var(--warnc)' }}>{g.verif.done}/3</b><span>vérification</span></div>
                <div><b style={{ color: g.veto ? 'var(--badc)' : 'var(--greenc)' }}>{g.masked ? '—' : g.veto ? 'NON' : g.mgrVerdict === 'reserve' ? 'rés.' : g.mgrVerdict ? 'OUI' : '—'}</b><span>manager</span></div>
              </div>
              <div className="vds">
                {subs.map((sc) => <span key={sc.id} className={`rec-vd ${sc.verdict}`}>{sc.author.split(/\s+/)[0]} · {sc.verdict === 'oui' ? 'OUI' : sc.verdict === 'reserve' ? 'RÉSERVE' : 'NON'}</span>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── « balle dans le camp » ──────────────────────────────────
export function RecCharge({ apps, reqId, onOpen }: {
  apps: RecApplicationDTO[];
  reqId: string;
  onOpen: (id: string) => void;
}) {
  const list = recActiveApps(apps, reqId);
  const owner = (a: RecApplicationDTO) =>
    a.waitOn === 'rh' ? 'rh' : a.waitOn === 'mgr' ? 'mgr' : a.stage === 'OFFRE' ? 'cand' : 'none';
  const LANES: Array<[string, string, string, string]> = [
    ['mgr', 'Balle côté manager', '#7037ef', 'présélection → entretiens'],
    ['rh', 'Balle côté owner', '#0c0c0e', 'vérification → offre'],
    ['cand', 'Côté candidat / agenda', '#2a6fdb', 'en attente externe'],
    ['none', 'Rien de dû', '#74747e', 'au fil de l’eau'],
  ];
  return (
    <div className="r2-lanes">
      {LANES.map(([key, lbl, c, sub], li) => {
        const col = list.filter((a) => owner(a) === key);
        return (
          <div key={key} className="rec-col r2-lane" style={{ '--sc': c, animationDelay: `${li * 70}ms` } as React.CSSProperties}>
            <div className="rec-col-h"><span className="d"></span>{lbl}<span className="n">{col.length}</span></div>
            <div className="r2-sub">{sub}</div>
            <div className="rec-col-b">
              {col.map((a) => {
                const st = recStageOf(a.stage);
                return (
                  <div key={a.id} className="rec-card" style={{ '--sc': c, cursor: 'pointer' } as React.CSSProperties} onClick={() => onOpen(a.id)}>
                    <div className="top">
                      <RecAv app={a} sm />
                      <div style={{ minWidth: 0 }}>
                        <div className="nm">{a.candidate.firstName} {a.candidate.lastName}</div>
                        <div className="rl">{recNextAction(a)}</div>
                      </div>
                    </div>
                    <div className="bot">
                      <span className="rec-est" style={{ background: `color-mix(in srgb,${st.c} 14%,transparent)`, color: st.c, fontSize: 9.5, padding: '3px 8px' }}><i></i>{st.lbl}</span>
                      <span style={{ marginLeft: 'auto' }}><RecDaysPill since={a.stageSince} /></span>
                    </div>
                  </div>
                );
              })}
              {!col.length && <div className="rec-empty-col">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
